import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * Lightweight scroll-reveal wrapper. Uses IntersectionObserver to flip a
 * `data-inview` attribute on the element the first time it crosses into the
 * viewport; CSS keyframes handle the animation itself (see theme.css).
 *
 * We intentionally avoid a full animation library here — CSS + IO is fast,
 * zero-dep, and respects `prefers-reduced-motion` via the global rule in
 * theme.css that collapses transitions to ~0ms.
 */
export function Reveal({
  children,
  delay = 0,
  y = 20,
  as: Tag = "div",
  className,
  once = true,
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
  once?: boolean;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Environments without IntersectionObserver (old jsdom, some bots) —
    // show immediately rather than leaving content invisible.
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }

    // Respect reduced-motion — skip the reveal entirely.
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      setSeen(true);
      return;
    }

    // If the element is already in view on mount (e.g. above the fold on
    // initial load, or Playwright's fullPage capture that never scrolls),
    // flip it to "seen" synchronously rather than relying on a scroll.
    const rect = node.getBoundingClientRect();
    const vh = window.innerHeight || 800;
    if (rect.top < vh + 200) {
      setSeen(true);
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setSeen(true);
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            setSeen(false);
          }
        }
      },
      { rootMargin: "200px 0px 200px 0px", threshold: 0 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [once]);

  const style: CSSProperties = {
    transform: seen ? "translateY(0)" : `translateY(${y}px)`,
    opacity: seen ? 1 : 0,
    transition: `opacity 620ms cubic-bezier(0.2, 0, 0.2, 1) ${delay}ms, transform 680ms cubic-bezier(0.2, 0, 0.2, 1) ${delay}ms`,
    willChange: "opacity, transform",
  };

  const Element = Tag as React.ElementType;
  return (
    <Element
      ref={ref as unknown as React.Ref<HTMLElement>}
      style={style}
      className={className}
    >
      {children}
    </Element>
  );
}

/**
 * Rotates through a list of strings, swapping after `interval` ms with a
 * subtle slide-fade. Used for the hero's rotating verb ("Always." → "Today."
 * → "At 3am." → "In April.") and similar accent type.
 */
export function Rotator({
  words,
  interval = 2200,
  className,
  style,
}: {
  words: readonly string[];
  interval?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const [i, setI] = useState(0);
  const [visible, setVisible] = useState(true);
  const FADE = 600; // ms for each half of the crossfade

  useEffect(() => {
    if (words.length <= 1) return;
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setI((n) => (n + 1) % words.length);
        setVisible(true);
      }, FADE);
    }, interval);
    return () => clearInterval(id);
  }, [words, interval]);

  return (
    <span
      aria-live="polite"
      className={className}
      style={{
        display: "inline-block",
        whiteSpace: "nowrap",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(-5px)",
        transition: `opacity ${FADE}ms ease-in-out, transform ${FADE}ms ease-in-out`,
        ...style,
      }}
    >
      {words[i]}
    </span>
  );
}

/**
 * Marquee strip that scrolls its children horizontally forever. Used for the
 * rotating-testimonials ribbon. Pauses on hover, respects
 * `prefers-reduced-motion`. The children are duplicated so the loop is
 * seamless.
 */
export function Marquee({
  children,
  speed = 40,
  className,
}: {
  children: ReactNode;
  /** Seconds for one full pass across the viewport. */
  speed?: number;
  className?: string;
}) {
  return (
    <div
      className={["proofly-marquee", className].filter(Boolean).join(" ")}
      style={
        {
          ["--proofly-marquee-duration" as string]: `${speed}s`,
        } as CSSProperties
      }
    >
      <div className="proofly-marquee-track">
        <div className="proofly-marquee-group">{children}</div>
        <div className="proofly-marquee-group" aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}
